import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="pwa-install"
export default class extends Controller {
  static targets = ["notification"];

  connect() {
    this.deferredPrompt = null;
    this.setupInstallPrompt();
  }

  setupInstallPrompt() {
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e;

      // Check if user hasn't dismissed the prompt before
      const dismissed = localStorage.getItem("pwa-install-dismissed");
      const installed = localStorage.getItem("pwa-installed");

      if (!dismissed && !installed) {
        // Show the custom install prompt after a short delay
        setTimeout(() => {
          this.showInstallNotification();
        }, 3000); // Show after 3 seconds
      }
    });

    // Listen for app installed event
    window.addEventListener("appinstalled", (e) => {
      console.log("PWA was installed");
      localStorage.setItem("pwa-installed", "true");
      this.hideInstallNotification();
    });

    // Check if already in standalone mode (PWA is installed)
    if (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    ) {
      localStorage.setItem("pwa-installed", "true");
      return;
    }

    // For iOS devices, show install instructions after delay
    if (this.isIOS() && !localStorage.getItem("pwa-install-dismissed")) {
      setTimeout(() => {
        this.showIOSInstallNotification();
      }, 5000); // Show after 5 seconds for iOS
    }

    // For mobile devices without beforeinstallprompt event, show install notification
    setTimeout(() => {
      const dismissed = localStorage.getItem("pwa-install-dismissed");
      const installed = localStorage.getItem("pwa-installed");

      if (!dismissed && !installed && !this.deferredPrompt && this.isMobile()) {
        this.showInstallNotification();
      }
    }, 8000); // Show after 8 seconds if no prompt detected on mobile
  }

  showInstallNotification() {
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.remove("hidden");
      this.notificationTarget.classList.add("animate-slide-up");
    }
  }

  showIOSInstallNotification() {
    if (this.hasNotificationTarget) {
      // Update content for iOS
      const content = this.notificationTarget.querySelector(
        '[data-pwa-install-target="content"]'
      );
      if (content) {
        content.innerHTML = `
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-900">Install Jubilee Pensions App</h3>
              <p class="text-xs text-gray-600 mt-1">For the best experience, add this app to your home screen:</p>
              <ol class="text-xs text-gray-600 mt-2 space-y-1">
                <li>1. Tap the <strong>Share</strong> button at the bottom of Safari</li>
                <li>2. Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                <li>3. Tap <strong>"Add"</strong> to confirm</li>
              </ol>
            </div>
          </div>
        `;
      }
      this.showInstallNotification();
    }
  }

  showMobileInstallNotification() {
    if (this.hasNotificationTarget) {
      // Update content for mobile browsers
      const content = this.notificationTarget.querySelector(
        '[data-pwa-install-target="content"]'
      );
      if (content) {
        content.innerHTML = `
          <div class="flex items-start space-x-3">
            <div class="flex-shrink-0">
              <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-900">Install Jubilee Pensions App</h3>
              <p class="text-xs text-gray-600 mt-1">Add this app to your home screen for quick access:</p>
              <ol class="text-xs text-gray-600 mt-2 space-y-1">
                <li>1. Tap your browser's <strong>menu button</strong></li>
                <li>2. Look for <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong></li>
                <li>3. Tap <strong>"Add"</strong> or <strong>"Install"</strong></li>
              </ol>
            </div>
          </div>
        `;
      }
      this.showInstallNotification();
    }
  }

  installApp() {
    if (!this.deferredPrompt) {
      console.log("No deferred prompt available, showing manual instructions");

      // If on iOS, show iOS install instructions
      if (this.isIOS()) {
        this.showIOSInstallNotification();
        return;
      }

      // For other mobile browsers without beforeinstallprompt, show general instructions
      if (this.isMobile()) {
        this.showMobileInstallNotification();
        return;
      }

      // On desktop, just dismiss the notification
      this.hideInstallNotification();
      return;
    }

    // Show the install prompt
    this.deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    this.deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === "accepted") {
        console.log("User accepted the install prompt");
        localStorage.setItem("pwa-installed", "true");
      } else {
        console.log("User dismissed the install prompt");
        localStorage.setItem("pwa-install-dismissed", "true");
      }
      this.deferredPrompt = null;
      this.hideInstallNotification();
    });
  }

  dismissNotification() {
    localStorage.setItem("pwa-install-dismissed", "true");
    this.hideInstallNotification();
  }

  hideInstallNotification() {
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.add("hidden");
      this.notificationTarget.classList.remove("animate-slide-up");
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  isMobile() {
    return (
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
  }

  // Debug method to reset install state
  resetInstallState() {
    localStorage.removeItem("pwa-install-dismissed");
    localStorage.removeItem("pwa-installed");
    console.log("PWA install state reset");
  }
}
